// Authenticated API client used throughout the app.
//
// Wraps fetch with:
//   - JWT bearer token from localStorage
//   - JSON Content-Type by default (auto-removed for FormData)
//   - 401 handler that clears auth and reloads to send the user to /login
//   - Returns null on non-2xx instead of throwing
//
// All API calls in App.tsx and downstream component files should go
// through this so auth handling stays in one place.

export const apiCall = async (path: string, options: RequestInit = {}): Promise<any> => {
  const token = localStorage.getItem("kontractai_token");
  const headers: Record<string, string> = { "Content-Type": "application/json", ...(options.headers as Record<string, string> | undefined) };
  if (token) headers["Authorization"] = `Bearer ${token}`;
  // X-Client-Context: optional per-session narrowing of req.clientScope on the
  // backend. Sourced from sessionStorage, written by ClientScopeContext when
  // the user picks a client. Absent or "all" → backend uses full user scope.
  try {
    const ctx = sessionStorage.getItem("kontractai_client_scope");
    if (ctx && ctx !== "all" && !headers["X-Client-Context"]) {
      headers["X-Client-Context"] = ctx;
    }
  } catch { /* sessionStorage unavailable — skip silently */ }
  if (options.body instanceof FormData) delete headers["Content-Type"];
  try {
    const res = await fetch(`/api${path}`, { ...options, headers });
    if (res.status === 401) {
      localStorage.removeItem("kontractai_token");
      localStorage.removeItem("kontractai_user");
      window.location.reload();
      return null;
    }
    if (!res.ok) return null;
    return res.json();
  } catch {
    return null;
  }
};

// ── Authenticated document access (AUTHDOCSERVE_20260625) ────────────────────
// The open `/uploads/*` static mount was removed; documents are served only by
// the authenticated by-ID endpoints in backend routes/documents.js. Auth is
// Bearer-only, so browser-native opens (window.open / <a href> / ms-word) cannot
// attach the header. We therefore fetch the bytes WITH the bearer token and turn
// them into a Blob URL for view/download; the one exception is "Open in Word",
// where Word fetches the URL itself, so we mint a short-TTL signed token.

export type DocType = "version" | "executed" | "executed_word" | "template";

// WHY THIS RETURNS A REASON AND NOT JUST null (Certara, 2026-10-09).
// It used to return null for every failure, so the two callers below said
// "Unable to open document (not found or access denied)" whatever had happened.
// On an installation whose disk does not survive a restart — Certara's, until
// durable storage is attached — the common failure is neither of those: the
// record is fine, the reader is allowed, and the FILE IS GONE. That reads as a
// permissions puzzle and sent a real investigation down the wrong path for a
// day. The server now says `file_missing` with a sentence; this carries it.
type DocFetch = { blob: Blob } | { blob: null; message: string };

const GENERIC_DOC_FAILURE = "Unable to open document (not found or access denied).";

async function fetchDocBlob(dtype: DocType, id: string | number, disposition: "inline" | "attachment"): Promise<DocFetch> {
  const token = localStorage.getItem("kontractai_token");
  const headers: Record<string, string> = {};
  if (token) headers["Authorization"] = `Bearer ${token}`;
  try {
    const ctx = sessionStorage.getItem("kontractai_client_scope");
    if (ctx && ctx !== "all") headers["X-Client-Context"] = ctx;
  } catch { /* ignore */ }
  try {
    const res = await fetch(`/api/documents/file?dtype=${dtype}&id=${encodeURIComponent(String(id))}&disposition=${disposition}`, { headers });
    if (!res.ok) {
      // Prefer the server's own sentence when it sent one. Keyed off the error
      // CODE, never the prose, so the two cannot drift apart.
      let message = GENERIC_DOC_FAILURE;
      try {
        const body = await res.json();
        if (body && body.error === "file_missing" && typeof body.message === "string") message = body.message;
      } catch { /* not JSON — keep the generic sentence */ }
      return { blob: null, message };
    }
    return { blob: await res.blob() };
  } catch {
    return { blob: null, message: GENERIC_DOC_FAILURE };
  }
}

// Open a document inline in a new tab (PDF view). Opens the tab synchronously
// (pre-fetch) to avoid popup blocking, then points it at the fetched Blob URL.
export const viewDocument = async (dtype: DocType, id: string | number): Promise<boolean> => {
  const win = window.open("", "_blank");
  const r = await fetchDocBlob(dtype, id, "inline");
  if (!r.blob) { if (win) win.close(); alert(r.message); return false; }
  const url = URL.createObjectURL(r.blob);
  if (win) win.location.href = url; else window.open(url, "_blank");
  setTimeout(() => URL.revokeObjectURL(url), 60000);
  return true;
};

// Download a document (attachment) by id.
export const downloadDocument = async (dtype: DocType, id: string | number, fileName?: string): Promise<boolean> => {
  const r = await fetchDocBlob(dtype, id, "attachment");
  if (!r.blob) { alert(r.message); return false; }
  const url = URL.createObjectURL(r.blob);
  const a = document.createElement("a");
  a.href = url; a.download = fileName || "document";
  document.body.appendChild(a); a.click(); a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 60000);
  return true;
};

// Download the retained as-sent copy of a counterparty send — the EXACT bytes
// that were attached to the outbound email, keyed to the send-log id.
export const downloadAsSentCopy = async (requestId: string, sendId: string, fileName?: string): Promise<boolean> => {
  const token = localStorage.getItem("kontractai_token");
  const headers: Record<string, string> = {};
  if (token) headers["Authorization"] = `Bearer ${token}`;
  try {
    const ctx = sessionStorage.getItem("kontractai_client_scope");
    if (ctx && ctx !== "all") headers["X-Client-Context"] = ctx;
  } catch { /* ignore */ }
  try {
    const res = await fetch(`/api/requests/${encodeURIComponent(requestId)}/sends/${encodeURIComponent(sendId)}/as-sent`, { headers });
    if (!res.ok) { alert("Unable to download the as-sent copy (not found or access denied)."); return false; }
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = fileName || "as-sent.docx";
    document.body.appendChild(a); a.click(); a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 60000);
    return true;
  } catch {
    alert("Unable to download the as-sent copy.");
    return false;
  }
};

// Returns a Blob for callers that need the raw bytes (e.g. attaching a PDF to AI chat).
// Keeps its Blob-or-null shape: these callers have no screen to put a sentence on.
export const getDocumentBlob = async (dtype: DocType, id: string | number): Promise<Blob | null> =>
  (await fetchDocBlob(dtype, id, "inline")).blob;

// Open a docx in the desktop Word app. Word fetches the URL itself (no bearer),
// so mint a short-TTL signed token first via the authenticated mint endpoint.
export const openInWord = async (dtype: DocType, id: string | number): Promise<boolean> => {
  const res = await apiCallEx(`/documents/download-token`, { method: "POST", body: JSON.stringify({ dtype, id, disposition: "inline" }) });
  if (!res.ok || !res.data?.token) { alert("Unable to open in Word (not found or access denied)."); return false; }
  window.location.href = `ms-word:ofe|u|${window.location.origin}/api/documents/open?dt=${encodeURIComponent(res.data.token)}`;
  return true;
};

// Variant of apiCall that EXPOSES the HTTP status and error body instead of
// collapsing every non-2xx to null. Use this where the caller must distinguish
// outcomes — e.g. the platform tenant-create flow needs to show the friendly
// "admin email already in use" message on a 409 rather than a generic failure.
// Same auth/401 handling as apiCall; never throws.
export interface ApiResult { ok: boolean; status: number; data: any; upstream: boolean }

/**
 * How a 401 should be handled.
 *
 *   "reload" (default) — clear the dead session and reload to the login screen.
 *                        Correct for reads: there is nothing to lose.
 *   "return"           — clear the dead session but DO NOT navigate; hand the
 *                        401 back so the caller can tell the user what happened
 *                        to their in-flight change.
 *
 * WHY THE OPT-OUT EXISTS. `window.location.reload()` in the middle of a mutation
 * is indistinguishable, from the user's side, from the mutation silently failing:
 * the dialog vanishes, the page comes back logged out, and after signing in again
 * the old value is still there with no error ever shown. A user cannot tell
 * "your session expired and nothing was saved" from "it saved and did not work".
 * That ambiguity cost a real investigation on the AI-routing console.
 *
 * WHAT IS *NOT* OPTIONAL. The tokens are cleared on EVERY 401 regardless of this
 * setting. The session is dead either way and a dead credential must not sit in
 * localStorage because a caller asked to keep its dialog open. Only the
 * NAVIGATION is opt-out; the security-relevant half is unconditional.
 */
export type On401 = "reload" | "return";

/**
 * Did this error response come from KontractAI at all?
 *
 * THE INCIDENT (Certara, 2026-10-08). "Confirm & Activate Template" reported
 * "You do not have permission to activate this template." for an administrator
 * who demonstrably held the permission. The 403 was not KontractAI's: it was the
 * Azure Application Gateway (WAF v2) block page, and the request never reached
 * the application. The likely trigger was SQL-injection rule families matching
 * bracketed placeholder text in the PUT body, e.g.
 * "[INSERT FULL CORPORATE LEGAL NAME OF CUSTOMER]".
 *
 * So the screen told the user the one thing that was certainly false, and sent
 * an administrator looking for a permission problem that did not exist. The
 * status code alone cannot tell the two apart — a gateway and an authorization
 * failure both say 403. The BODY can.
 *
 * THE INFERENCE, and it is checkable rather than assumed. Every application
 * error response in this codebase is JSON: `res.status(4xx|5xx).json({ error })`.
 * There is no `sendStatus`, no `.send(` and no `.end(` on any error path in
 * backend/src — verified with
 *   grep -rn "sendStatus(4\|status(4[0-9][0-9])\.\(end\|send\)(" backend/src/
 * which returns nothing, against 72 `status(403).json` call sites in routes
 * alone. An error whose body is not a JSON OBJECT therefore did not come from
 * KontractAI's application layer. That is the whole test, and it is why this
 * reads the body shape and not the Content-Type header: an intermediary is free
 * to send `application/json` with an HTML body, and some do.
 *
 * An EMPTY body counts as upstream for the same reason — the application never
 * produces one on an error. If that premise ever stops holding, this function is
 * the one place to change.
 */
export function isUpstreamError(ok: boolean, body: string): boolean {
  if (ok) return false;
  const text = (body || "").trim();
  if (text === "") return true;
  try {
    const parsed = JSON.parse(text);
    // KontractAI's shape is a JSON OBJECT — `{ error: ... }`. A bare
    // string/number/array that happens to be valid JSON is not, and neither is
    // an HTML page. The Array check is not pedantry: `typeof [] === "object"`,
    // so without it a gateway answering `[]` would be read as the application's
    // own error. A test caught exactly that.
    return !(parsed && typeof parsed === "object" && !Array.isArray(parsed));
  } catch {
    return true;
  }
}

/**
 * Turn an apiCallEx result into a message an operator can act on.
 *
 * WHY IT IS HERE AND NOT IN EACH SCREEN. `apiCall` collapses 400/401/403/404/
 * 409/422/500 AND a network failure into the same `null`, so a caller using it
 * cannot tell "you lack permission" from "that name is taken" from "the server
 * crashed" from "you are offline". The widespread `r?.error || "Failed"` idiom
 * reads as though it surfaces the server's reason; with `apiCall` it structurally
 * cannot, because `r` is null exactly when there is a reason to show. Screens
 * that must report a failure use apiCallEx and this, so the wording of a failed
 * save lives in one place rather than being re-invented per screen (and so a
 * fixed screen cannot drift back to showing nothing).
 */
export function errorText(r: ApiResult, fallback: string): string {
  // FIRST, before anything reads the status. A gateway 403 and an authorization
  // 403 are the same number, and naming the wrong one costs an investigation.
  // There is also no server body to prefer here: if there were, this would not
  // be an upstream error.
  if (r.upstream) {
    return r.status === 403
      ? "This request was blocked by your organization's network gateway or firewall before it reached KontractAI, so nothing was saved. This is not a permissions problem. Your IT team may need to allow this request — text typed into a contract or template can look like an attack to a security filter."
      : `This request was stopped by your organization's network gateway or firewall before it reached KontractAI (it returned ${r.status}), so nothing was saved. This is not a problem with your account.`;
  }
  const fromServer = r.data && (r.data.error || r.data.message);
  if (fromServer) return String(fromServer);
  if (r.status === 401) return "Your session expired. Sign in again — the change was not saved.";
  if (r.status === 403) return "You do not have permission to make this change.";
  if (r.status === 0) return `${fallback} The server could not be reached.`;
  return `${fallback} (server returned ${r.status})`;
}

export const apiCallEx = async (path: string, options: RequestInit = {}, on401: On401 = "reload"): Promise<ApiResult> => {
  const token = localStorage.getItem("kontractai_token");
  const headers: Record<string, string> = { "Content-Type": "application/json", ...(options.headers as Record<string, string> | undefined) };
  if (token) headers["Authorization"] = `Bearer ${token}`;
  try {
    const ctx = sessionStorage.getItem("kontractai_client_scope");
    if (ctx && ctx !== "all" && !headers["X-Client-Context"]) headers["X-Client-Context"] = ctx;
  } catch { /* sessionStorage unavailable — skip silently */ }
  if (options.body instanceof FormData) delete headers["Content-Type"];
  try {
    const res = await fetch(`/api${path}`, { ...options, headers });
    if (res.status === 401) {
      // Unconditional: the session is dead, so the credential goes, whatever the
      // caller asked for. Only the navigation below is opt-out.
      localStorage.removeItem("kontractai_token");
      localStorage.removeItem("kontractai_user");
      if (on401 === "reload") window.location.reload();
      return { ok: false, status: 401, data: null, upstream: false };
    }
    // Read the body ONCE as text, then decide. res.json() would throw the body
    // away on a parse failure, which is precisely the evidence that identifies a
    // gateway block — the old `catch { /* empty/non-JSON body */ }` discarded it.
    let body = "";
    try { body = await res.text(); } catch { /* body unreadable — treat as empty */ }
    let data: any = null;
    try { data = body ? JSON.parse(body) : null; } catch { /* non-JSON body */ }
    return { ok: res.ok, status: res.status, data, upstream: isUpstreamError(res.ok, body) };
  } catch {
    // Network-level failure: the request did not complete at all, so there is no
    // body to judge and no gateway verdict to report.
    return { ok: false, status: 0, data: null, upstream: false };
  }
};
